import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Palette, Trash2, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminColoring() {
  const [pages, setPages] = useState([]);
  const [total, setTotal] = useState(0);
  const [today, setToday] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      const { data } = await api.get(`/admin/coloring/pages?${params.toString()}`);
      setPages(data.pages || []); setTotal(data.total || 0); setToday(data.today || 0);
    } catch {
      toast.error("Failed to load coloring pages");
    }
    setLoading(false);
  };

  useEffect(() => {
    api.get("/admin/coloring/settings").then(({ data }) => setSettings(data)).catch(() => {});
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(refresh, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const del = async (id) => {
    if (!window.confirm("Hide this coloring page from public view? (Image stays cached on disk for safety.)")) return;
    try {
      await api.delete(`/admin/coloring/pages/${id}`);
      toast.success("Hidden");
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/coloring/settings", {
        enabled: !!settings.enabled,
        daily_cap: Number(settings.daily_cap) || 5,
        max_prompt_chars: Number(settings.max_prompt_chars) || 240,
        share_publicly: !!settings.share_publicly,
        model_name: settings.model_name || "gemini-3.1-flash-image-preview",
      });
      setSettings(data);
      toast.success("Settings saved");
    } catch {
      toast.error("Could not save settings");
    }
    setSaving(false);
  };

  return (
    <div data-testid="admin-coloring">
      <header className="mb-6">
        <h1 className="font-accent text-3xl font-bold flex items-center gap-2"><Palette className="w-7 h-7 text-[#5a8a6f]" /> Coloring Pages</h1>
        <p className="text-sm text-[#6b7280] mt-1">{total} total · {today} today. Generated by Gemini Nano Banana.</p>
      </header>

      {settings && (
        <section className="bg-white rounded-3xl p-5 mb-6 border border-[#f4e4c6]" data-testid="coloring-settings">
          <h2 className="font-accent text-xl font-bold mb-3">Settings</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="text-sm">
              <div className="font-semibold mb-1">Status</div>
              <select value={settings.enabled ? "1" : "0"} onChange={(e) => setSettings({ ...settings, enabled: e.target.value === "1" })} className="inp" data-testid="coloring-setting-enabled">
                <option value="1">Enabled</option>
                <option value="0">Disabled</option>
              </select>
            </label>
            <label className="text-sm">
              <div className="font-semibold mb-1">Daily cap per visitor</div>
              <input type="number" min="1" max="50" value={settings.daily_cap} onChange={(e) => setSettings({ ...settings, daily_cap: e.target.value })} className="inp" data-testid="coloring-setting-cap" />
            </label>
            <label className="text-sm">
              <div className="font-semibold mb-1">Max prompt chars</div>
              <input type="number" min="50" max="1000" value={settings.max_prompt_chars} onChange={(e) => setSettings({ ...settings, max_prompt_chars: e.target.value })} className="inp" data-testid="coloring-setting-maxchars" />
            </label>
            <label className="text-sm">
              <div className="font-semibold mb-1">Share publicly</div>
              <select value={settings.share_publicly ? "1" : "0"} onChange={(e) => setSettings({ ...settings, share_publicly: e.target.value === "1" })} className="inp" data-testid="coloring-setting-share">
                <option value="1">Yes — show in public gallery</option>
                <option value="0">No — private only</option>
              </select>
            </label>
          </div>
          <button onClick={saveSettings} disabled={saving} className="btn-primary mt-3 inline-flex" data-testid="coloring-settings-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save settings"}
          </button>
        </section>
      )}

      <section className="bg-white rounded-3xl p-5 mb-4 border border-[#f4e4c6]">
        <label className="text-sm block">
          <div className="font-semibold mb-1 flex items-center gap-1"><Search className="w-4 h-4" /> Search prompts</div>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find words..." className="inp" data-testid="coloring-search" />
        </label>
      </section>

      {loading ? (
        <div className="text-[#6b7280] text-center py-12">Loading…</div>
      ) : pages.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center text-[#6b7280]" data-testid="coloring-empty">
          <Palette className="w-10 h-10 mx-auto mb-3 text-[#cbd5e1]" />
          <div className="font-semibold mb-1">No coloring pages yet</div>
          <div className="text-sm">When kids start drawing, you'll see them here.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="coloring-grid">
          {pages.map((p) => (
            <article key={p.id} className={`bg-white rounded-2xl p-3 border ${p.deleted ? "opacity-50 border-red-200" : "border-[#f4e4c6]"}`} data-testid={`coloring-card-${p.id}`}>
              <a href={p.image_url} target="_blank" rel="noopener noreferrer">
                <img src={p.image_url.startsWith("http") ? p.image_url : `${process.env.REACT_APP_BACKEND_URL}${p.image_url}`} alt={p.prompt} className="w-full aspect-square object-contain rounded-xl bg-white border border-[#f4e4c6]" />
              </a>
              <p className="text-sm font-semibold text-[#3a4a55] mt-2 line-clamp-2">"{p.prompt}"</p>
              <div className="flex items-center justify-between mt-2 text-xs text-[#6b7280]">
                <span>{p.character_name || "No character"} · {new Date(p.created_at).toLocaleDateString()}</span>
                {!p.deleted && (
                  <button onClick={() => del(p.id)} className="p-1.5 rounded-full hover:bg-red-50 text-red-600" data-testid={`coloring-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
              <div className="text-xs text-[#6b7280] mt-1">Visitor: <span className="font-mono">{(p.visitor_id || "").slice(0, 14)}...</span></div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
